import _ from "lodash";

import * as k4util from "@/utils/k4util";

import * as path from "../path";

export default class Node {
	static get modelName() {
		return "Node";
	}

	constructor(k4node) {
		this.guid = k4node.guid() || null;
		this.name = k4node.name() || null;
		this.readableName = k4util.humanizeNodeName(k4node);
		this.path = k4node.path() || null;
		this.cls = k4node.cls() || null;

		const parent = k4util.closestParentDevice(k4node);
		this.parentDevice = parent ? path.$ref(parent.path()) : null;

		// Meta is occasionally missing from malformed nodes. Mostly seen in
		// command nodes that weren't added correctly and are missing argument meta.
		let meta = k4node.meta() || {};

		// Use `meta.type` instead of `node.type()` since they often report
		// different values and the meta version is the more useful of the two.
		// Ex. "string" from `node.type()` vs `time` from `meta.type`.
		this.type = meta.type || null;

		this.group = meta.group || null;

		// Capture node value, if applicable.
		if (!_.isNil(this.type) && this.type !== "object")
			this.value = k4node.value();

		// Create JSON references to various "standard" child node types for easy
		// access from the parent.
		for (const childType of ["commands", "properties", "variables", "events"]) {
			let childTypeNodes = k4node.child(childType);
			childTypeNodes = childTypeNodes
				? _.values(childTypeNodes.children())
				: [];
			for (const child of childTypeNodes) {
				if (_.isUndefined(this[childType])) this[childType] = {};
				this[childType][child.name()] = path.$ref(child.path());
			}
		}

		// Would be better if these could be getters, but they require an actual k4model node
		this.isVariable = k4util.isVariable(k4node);

		// Catch-all children property with references too all valid child nodes
		this.children = _.map(k4util.childNodes(k4node), node =>
			path.$ref(node.path())
		);

		this._errors = [];
		this._valid = undefined;
	}

	_validate() {
		// Override in subclass
		// If errors can only be captured in subclass constructor, errors can be
		// pushed with `addValidationError` to be caught by lazy validation.
		this._valid = _.isEmpty(this._errors);
	}

	get valid() {
		// Lazy validation
		if (_.isUndefined(this._valid)) this._validate();

		return (
			this._valid &&
			_.every(this.children, "valid") &&
			_.every(this.commands, "valid") &&
			_.every(this.properties, "valid") &&
			_.every(this.variables, "valid") &&
			_.every(this.events, "valid")
		);
	}

	get errors() {
		let retval = _.concat(
			this._errors,
			..._.map(this.children, "errors"),
			..._.map(this.commands, "errors"),
			..._.map(this.properties, "errors"),
			..._.map(this.variables, "errors"),
			..._.map(this.events, "errors")
		);
		retval = _.compact(_.flatten(retval));
		return retval;
	}

	addValidationError(message) {
		this._errors.push(`${this.path}: ${message}`);
	}

	get isTime() {
		return k4util.isTypeTime(this);
	}

	get isDate() {
		return k4util.isTypeDate(this);
	}

	get isEvent() {
		return k4util.isTypeEvent(this);
	}

	get isCommand() {
		return k4util.isTypeCommand(this);
	}

	get isDelay() {
		return false;
	}

	get isInterval() {
		return false;
	}
}
